import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { searchTermAssociationFetcher } from '@/data/Content/_SearchTermAssociationFetcher';
import { useLocalization } from '@/data/Localization';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { TYPE_AHEAD_DELAY } from '@/data/config/TYPE_AHEAD_DELAY';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { HDM_CATALOG_SEO_PATHS, HDM_SEARCH_SEO_PATHS } from '@/data/constants/urlPathConstants';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getProductListQueryParameters } from '@/data/utils/getProductListQueryParameters';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { error as logError } from '@/data/utils/loggerUtil';
import { processError } from '@/data/utils/processError';
import { SiteContentResource } from 'integration/generated/query/SiteContentResource';
import type {
	CommonSuggestions,
	KeywordSuggestion,
} from 'integration/generated/query/data-contracts';
import querySiteContentResource from 'integration/generated/query/querySiteContentResource';
import { debounce, escapeRegExp } from 'lodash';
import { GetServerSidePropsContext } from 'next';
import {
	EVENT_SEARCH_SUBMITTED_TERM_KEY,
	EVENT_SEARCH_TYPE_KEY,
	GA4SearchType,
	decodeUrlDestination,
	getSearchType,
	getURLSearchType,
	useTrackSearchSubmit,
} from 'presentation/events/hooks/useTrackSearchSubmit';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

type Props = Parameters<SiteContentResource['findSuggestions']>;
type Query = Omit<Props[1], 'suggestType'> & {
	suggestType?: string[]; // Spec wrongly requires string, when it should be an array of strings, requiring this override
	physicalStoreId?: string;
	stLocId?: string;
};
type KeywordProps = Parameters<SiteContentResource['findKeywordSuggestionsByTerm']>;
type KeywordQuery = KeywordProps[2] & {
	physicalStoreId?: string;
	stLocId?: string;
};
export type SuggestionView = {
	identifier?: string;
	entry: EntryView[];
};
type EntryView = {
	label: string;
	href?: string;
};
const SUGGESTIONS_LIMIT = '5';

const fullDecodeString = (searchTerm: string, doubleEncodeSlash = false): string => {
	if (!searchTerm) return '';
	const encoded = encodeURIComponent(searchTerm);
	return encoded
		.replaceAll('%20', '+')
		.replaceAll('%28', '(')
		.replaceAll('%29', ')')
		.replaceAll('%2B', '+')
		.replaceAll('%3A', ':')
		.replaceAll('%3D', '=')
		.replaceAll('%3F', '?')
		.replaceAll('%2F', doubleEncodeSlash ? '%252F' : '/')
		.replaceAll('%26', '&')
		.replaceAll('%2C', ',');
};

const getSuggestionHref = (
	identifier: string | undefined,
	entryLabel: string,
	existingHref?: string
): string => {
	let finalHref = existingHref;
	if (!finalHref) {
		finalHref = `/${entryLabel}`;
	}
	const lowerCaseHref = finalHref.toLowerCase();
	switch (identifier) {
		case 'Product':
			if (!lowerCaseHref.startsWith(`/${HDM_CATALOG_SEO_PATHS.Product}/`)) {
				finalHref = `/${HDM_CATALOG_SEO_PATHS.Product}/${lowerCaseHref}`;
			}
			break;

		case 'Category':
			if (!lowerCaseHref.startsWith(`/${HDM_CATALOG_SEO_PATHS.Category}/`)) {
				finalHref = `/${HDM_CATALOG_SEO_PATHS.Category}${lowerCaseHref}`;
			}
			break;

		case 'Keyword':
		case 'Brand':
			finalHref = `/${HDM_SEARCH_SEO_PATHS.Search}/${fullDecodeString(entryLabel)}`;
			break;

		default:
			// Other types (e.g., History) use the existing href (which might be the default path)
			break;
	}

	return finalHref;
};

const dataMap = (
	searchTerm: string,
	data?: CommonSuggestions | KeywordSuggestion
): SuggestionView[] => {
	const regex = new RegExp(escapeRegExp(searchTerm), 'ig');
	const normalizedTerm = searchTerm.toLowerCase();
	return (
		data?.suggestionView
			?.map(({ identifier, entry }) => ({
				identifier,
				entry:
					entry
						?.filter(({ term, name, fullPath }: any) => {
							const entryLabel = term || fullPath || name;
							// Brands match only when the label starts with the search term;
							// other suggestion types keep the "includes" match.
							if (identifier === 'Brand') {
								return entryLabel?.toLowerCase().startsWith(normalizedTerm);
							}
							return entryLabel?.match(regex);
						})
						.map(({ fullPath, term, name, seo }: any) => {
							const entryLabel = fullPath || term || name;
							const entryHref = getSuggestionHref(identifier, entryLabel, seo?.href);

							return {
								label: entryLabel,
								type: identifier,
								...(entryHref ? { href: entryHref } : {}),
							};
						}) || [],
			}))
			.filter(({ entry }) => entry.length > 0) || []
	);
};

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		query,
		params = {},
	}: {
		storeId: Props[0];
		query: Query;
		params: Props[2];
	}): Promise<ReturnType<SiteContentResource['findSuggestions']> | void> => {
		try {
			const data = await querySiteContentResource(pub).findSuggestions(
				storeId,
				query as Props[1],
				params
			);
			return data;
		} catch (error) {
			logError(context?.req, 'SearchNavigation: fetcher: error: %o', error);
		}
	};

const submitSearchTerm = async (
	notifyError: ReturnType<typeof useNotifications>['notifyError'],
	args: Parameters<ReturnType<typeof searchTermAssociationFetcher>>[0]
) => {
	try {
		return await searchTermAssociationFetcher(true)(args);
	} catch (e) {
		notifyError(processError(e as TransactionErrorResponse));
		return undefined;
	}
};
const keywordFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		term,
		query,
		params = {},
	}: {
		storeId: KeywordProps[0];
		term: KeywordProps[1];
		query: KeywordQuery;
		params: KeywordProps[3];
	}): Promise<ReturnType<SiteContentResource['findKeywordSuggestionsByTerm']> | void> => {
		try {
			return await querySiteContentResource(pub).findKeywordSuggestionsByTerm(
				storeId,
				term,
				query as KeywordProps[2],
				params
			);
		} catch (error) {
			logError(context?.req, 'SearchNavigation: keywordFetcher: error: %o', error);
		}
	};
const EMPTY_SUGGESTIONS = {};
export const useSearchNavigation = () => {
	const { settings } = useSettings();
	const { user } = useUser();
	const SearchLocalization = useLocalization('Routes').Search;
	const router = useNextRouter();
	const { query } = router;
	const params = useExtraRequestParameters(false);
	const filteredParams = useMemo(() => getProductListQueryParameters(query), [query]);
	const [searchValue, setSearchValue] = useState<string>(
		() => filteredParams.searchTerm?.toString().trim() || ''
	);
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const { physicalStoreId, uniqueId: stLocId } = useDefaultStore();
	const { sendGA4Event, sendSocialMediaEvents } = useTrackSearchSubmit();
	const { notifyError } = useNotifications();
	const { data: categoriesAndBrands = EMPTY_SUGGESTIONS } = useSWR(
		storeId
			? [
					{
						storeId,
						query: {
							langId,
							suggestType: ['Category', 'Brand'],
							physicalStoreId,
							...(stLocId && { stLocId }),
							...getContractIdParamFromContext(user?.context),
						},
						params: {},
					},
			  ]
			: null,
		async ([props]) => fetcher(true)({ ...props, params }),
		{ use: [laggyMiddleWare] }
	);
	const { data = EMPTY_SUGGESTIONS } = useSWR(
		storeId && searchValue !== ''
			? [
					{
						storeId,
						term: fullDecodeString(searchValue, true).toLowerCase(),
						query: {
							langId,
							limit: SUGGESTIONS_LIMIT,
							catalogId,
							physicalStoreId,
							...(stLocId && { stLocId }),
							...getContractIdParamFromContext(user?.context),
						},
						params: {},
					},
			  ]
			: null,
		async ([props]) => keywordFetcher(true)({ ...props, params }),
		{ use: [laggyMiddleWare] }
	);
	const suggest = useMemo(
		() =>
			searchValue !== ''
				? [...dataMap(searchValue, data), ...dataMap(searchValue, categoriesAndBrands)]
				: [],
		[data, searchValue, categoriesAndBrands]
	);

	const pendingSearchRef = useRef<string | null>(null);

	const onSubmit = async ({ label: rawLabel, href }: EntryView) => {
		const label = fullDecodeString(rawLabel);
		if (label.trim() === '' || pendingSearchRef.current === rawLabel) {
			return;
		}
		pendingSearchRef.current = rawLabel;
		const releasePending = () => {
			if (pendingSearchRef.current === rawLabel) {
				pendingSearchRef.current = null;
			}
		};
		if (!href) {
			sessionStorage.removeItem(EVENT_SEARCH_TYPE_KEY);
		}
		const data = await submitSearchTerm(notifyError, { storeId, searchTerm: rawLabel, params });

		sessionStorage.setItem(EVENT_SEARCH_SUBMITTED_TERM_KEY, rawLabel);
		sendSocialMediaEvents({ searchTerm: rawLabel });

		const urlParts = data?.redirecturl
			?.replace(REGEX.SEARCH_REDIRECT_SUFFIX, EMPTY_STRING)
			.split('?');

		if (urlParts?.at(0)) {
			const payload = {
				searchTerm: rawLabel,
				searchType: getURLSearchType(urlParts[0]),
				urlDestination: decodeUrlDestination(window.location.origin + urlParts[0]),
				numberOfResults: -1,
			};
			sendGA4Event(payload);
			sessionStorage.removeItem(EVENT_SEARCH_TYPE_KEY);
			sessionStorage.removeItem(EVENT_SEARCH_SUBMITTED_TERM_KEY);
			const [pathname, hashValue] = (urlParts[0] || '').split('#');
			router
				.push(
					{
						pathname,
						query: urlParts[1],
						...(hashValue ? { hash: hashValue } : {}),
					},
					undefined,
					{ shallow: false }
				)
				.finally(releasePending);
		} else {
			const isSearchResultsPage = !href || href.startsWith(`/${HDM_SEARCH_SEO_PATHS.Search}/`);
			if (!isSearchResultsPage) {
				const searchType =
					(sessionStorage.getItem(EVENT_SEARCH_TYPE_KEY) as GA4SearchType | null) ||
					getSearchType(label);
				sessionStorage.removeItem(EVENT_SEARCH_TYPE_KEY);
				sessionStorage.removeItem(EVENT_SEARCH_SUBMITTED_TERM_KEY);
				sendGA4Event({
					searchTerm: rawLabel,
					searchType,
					urlDestination: decodeUrlDestination(window.location.origin + href),
					numberOfResults: -1,
				});
			}
			router
				.push(
					href
						? { pathname: href }
						: {
								pathname: `/${SearchLocalization.route.t()}/${label}`,
						  },
					undefined,
					{
						shallow: false,
					}
				)
				.finally(releasePending);
		}
	};

	const onInputChange = useMemo(
		() => debounce((_, value) => setSearchValue(value), TYPE_AHEAD_DELAY),
		[]
	);
	useEffect(() => {
		setSearchValue(filteredParams.searchTerm?.toString().trim() || '');
	}, [filteredParams.searchTerm]);

	return {
		suggest,
		searchValue,
		onInputChange,
		onSubmit,
	};
};
